import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2, Mail, Link2, Download, Copy } from "lucide-react";
import { useI18n } from '@/hooks/useI18n';
import { DashboardItinerary } from "@/lib/models/dashboard";
import { useState } from "react";
import { toast } from "@/hooks/useToast";

interface ShareResultPageProps {
  mode: 'email' | 'link';
  itinerary: DashboardItinerary;
  pdfBase64?: string;
  pdfFilename?: string;
  shareUrl?: string;
  linkPassword?: string;
  onClose: () => void;
}

export const ShareResultPage = ({
  mode,
  itinerary,
  pdfBase64,
  pdfFilename,
  shareUrl,
  linkPassword,
  onClose,
}: ShareResultPageProps) => {
  const { t } = useI18n();
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);

  const handleDownloadPdf = () => {
    if (!pdfBase64 || !pdfFilename) return;
    const byteChars = atob(pdfBase64);
    const byteArr = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteArr[i] = byteChars.charCodeAt(i);
    }
    const blob = new Blob([byteArr], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = pdfFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopyLink = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl);
    setCopiedLink(true);
    toast({ variant: 'info',
      title: t('common.copied'),
      description: t('itinerary.linkCopied'),
    });
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleCopyPassword = () => {
    if (!linkPassword) return;
    navigator.clipboard.writeText(linkPassword);
    setCopiedPassword(true);
    toast({ variant: 'info',
      title: t('common.copied'),
      description: t('itinerary.passwordCopied'),
    });
    setTimeout(() => setCopiedPassword(false), 2000);
  };

  return (
    <div className="space-y-6 px-6 py-8">
      {/* Success Header */}
      <div className="text-center space-y-3">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
          <CheckCircle2 className="h-8 w-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-semibold text-gray-900">
          {mode === 'email'
            ? t('itinerary.quoteSentSuccess')
            : t('itinerary.linkGeneratedSuccess')}
        </h2>
        <p className="text-gray-600">
          {mode === 'email'
            ? t('itinerary.quoteSentSuccessDescription')
            : t('itinerary.linkGeneratedSuccessDescription')}
        </p>
      </div>

      {/* Itinerary Info */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <p className="text-xs font-medium text-gray-500 uppercase mb-1">
          {t('common.itinerary')}
        </p>
        <p className="text-lg font-semibold text-gray-900">{itinerary.title}</p>
      </div>

      {/* PDF Download (if available) */}
      {pdfBase64 && pdfFilename && (
        <Alert className="bg-blue-50 border-blue-200">
          <Download className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-sm text-blue-800 flex items-center justify-between">
            <span>{t('itinerary.pdfGenerated')}</span>
            <Button
              onClick={handleDownloadPdf}
              variant="outline"
              size="sm"
              className="ml-4"
            >
              <Download className="h-4 w-4 mr-2" />
              {t('common.download')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Share Link (if available) */}
      {shareUrl && (
        <div className="space-y-3">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-xs font-medium text-gray-500 uppercase mb-2">
              {t('itinerary.shareableLink')}
            </p>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-white border border-gray-200 rounded px-3 py-2 text-sm font-mono text-gray-900 overflow-x-auto">
                {shareUrl}
              </div>
              <Button
                onClick={handleCopyLink}
                variant="outline"
                size="sm"
                className="flex-shrink-0"
              >
                {copiedLink ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    {t('common.copied')}
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    {t('common.copy')}
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Password Display (if set) */}
          {linkPassword && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-3">
              <p className="text-xs font-medium text-gray-500 uppercase mb-2">
                {t('itinerary.password')}
              </p>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-white border border-gray-200 rounded px-3 py-2 text-sm font-mono text-gray-900">
                  {linkPassword}
                </div>
                <Button
                  onClick={handleCopyPassword}
                  variant="outline"
                  size="sm"
                  className="flex-shrink-0"
                >
                  {copiedPassword ? (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      {t('common.copied')}
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-2" />
                      {t('common.copy')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-center pt-4">
        <Button
          onClick={onClose}
          size="lg"
          className="min-w-[200px]"
        >
          {t('common.close')}
        </Button>
      </div>
    </div>
  );
};
